"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Globe as Globe2, TrendingUp, Languages, Building2, ArrowRight, CircleCheck as CheckCircle2, Compass, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const pillars = [{
  icon: Languages,
  title: "Multi-Language & Hreflang Architecture",
  description: "I design technical architectures that prevent duplicate content issues across Arabic, English, and regional dialect variations while properly routing international visitors."
}, {
  icon: Compass,
  title: "Market-by-Market Search Intent",
  description: "Search behavior in the UK, Europe, or the US differs drastically from the GCC. I map local search intent and keyword semantics specific to each target territory."
}, {
  icon: Layers,
  title: "Cross-Border Technical SEO",
  description: "From ccTLD and subfolder structures to international CDN caching and hreflang tag automation, I implement the exact technical foundation global search engines demand."
}, {
  icon: TrendingUp,
  title: "Global Entity & Brand Authority",
  description: "I establish international authority through country-specific digital PR, geo-relevant citations, and global entity verification to compete against local market incumbents."
}];
const highlights = ["Direct strategy and implementation led by me—never handed off to an agency pool", "Targeted strategies for Dubai businesses scaling into UK, US, European, and MENA markets", "Zero template formulas; custom technical architecture built for your exact tech stack", "Transparent reporting with clear regional performance tracking and KPI clarity"];
export default function InternationalDubai() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="internationalseodubai" data-nav="light" className="bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <div className="flex justify-center mb-4">
            <Badge variant="secondary" className="bg-secondary text-foreground font-medium px-3 py-1 rounded-full border border-border">
              <Building2 className="size-3.5 mr-1.5 text-primary" />
              Dubai to Global Search Growth
            </Badge>
          </div>

          <motion.h2 initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="font-default text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-6">
            International SEO for Dubai Businesses Expanding Globally
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            I help UAE and Dubai-based companies break out of regional boundaries
            and win commercial rankings in competitive foreign markets. When you
            partner with me, you work directly with a senior SEO strategist dedicated
            to your sustainable organic expansion.
          </motion.p>
        </div>

        {/* 2-Column Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center mb-16">
          {/* Left Column: Context & Direct Value */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          x: -20
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 space-y-6">
            <div className="inline-flex p-3 rounded-xl bg-muted text-primary border border-border">
              <Globe2 className="size-8 text-primary" />
            </div>

            <h3 className="font-default text-2xl sm:text-3xl font-semibold text-foreground">
              Direct Strategic Guidance for High-Growth Global Ambitions
            </h3>

            <p className="font-sans text-muted-foreground text-sm sm:text-base leading-relaxed">
              Expanding outside the UAE requires more than simply translating English content
              into multiple languages. It demands rigorous domain structuring, market-specific
              search intent mapping, and authoritative international search signals.
            </p>

            <p className="font-sans text-muted-foreground text-sm sm:text-base leading-relaxed">
              I audit your current organic footprint, eliminate geographic cannibalization, and
              build an actionable cross-border roadmap tailored specifically to your target
              territories.
            </p>

            {/* Feature Checklist */}
            <ul className="space-y-3 pt-2">
              {highlights.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm text-foreground">
                  <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>)}
            </ul>

            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="rounded-md font-medium">
                <Link to="/InternationalSeoPage">
                  Explore International SEO
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/ContactPage">
                  Book a Consultation
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Strategic Pillars Cards */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pillars.map((pillar, i) => {
            const IconComponent = pillar.icon;
            return <motion.div key={pillar.title} data-index={i} initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 20
            }} whileInView={shouldReduceMotion ? undefined : {
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.1,
              ease: "easeOut"
            }}>
                  <Card className="h-full border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                    <CardContent className="p-6 flex flex-col justify-between h-full">
                      <div>
                        <div className="size-11 rounded-lg bg-muted flex items-center justify-center text-primary mb-4 border border-border">
                          <IconComponent className="size-5 text-primary" />
                        </div>
                        <h4 className="font-default text-lg font-semibold text-foreground mb-2">
                          {pillar.title}
                        </h4>
                        <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Bottom Banner Callout */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={shouldReduceMotion ? undefined : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-2xl bg-muted/60 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <p className="font-default font-semibold text-base sm:text-lg text-foreground">
              Ready to enter European, UK, or American search markets?
            </p>
            <p className="font-sans text-sm text-muted-foreground">
              Let&apos;s evaluate your current regional footprint and review international competitor landscapes.
            </p>
          </div>
          <Button asChild size="default" className="shrink-0 rounded-md">
            <Link to="/ContactPage">
              Discuss Your Expansion
              <ArrowRight className="size-4 ml-1.5" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}